import React,{useEffect,useRef,useState} from 'react';
import {FiCheck,FiLoader,FiPlay,FiX} from 'react-icons/fi';
import {resolveApiBase} from '../lib/transcriptionApi.js';
import {cleanupProtectedRanges,cleanupSignature,safeCleanupSuggestions} from '../lib/aiCleanup.js';
import {formatTime} from '../lib/editModel.js';

const labels={crew:'Crew / setup',false_start:'False start',filler:'Repetition / filler',off_topic:'Off-topic'};
const message=e=>e.name==='TimeoutError'?'AI Cleanup timed out. Try again.':e.message||'Could not analyse the transcript.';

/** Same interview-level cleanup control in both editing workspaces. Nothing removed until Apply. */
export default function AiCleanup({source,sourceId,priorityWordIds={},assemblyWordIds=[],
 getAccessToken,onApply,onPreview,disabled=false}){
 const [open,setOpen]=useState(false),[busy,setBusy]=useState(false);
 const [applying,setApplying]=useState(false),[error,setError]=useState('');
 const [suggestions,setSuggestions]=useState(null),[selected,setSelected]=useState([]);
 const request=useRef(null),signature=useRef('');
 const words=source?.words||[];
 const protectedRanges=cleanupProtectedRanges(words,priorityWordIds,assemblyWordIds,
  source?.cuts||[],source?.duration||0);
 const currentSignature=cleanupSignature(source)+':'+JSON.stringify(protectedRanges);
 useEffect(()=>{
  if(!open||!signature.current||currentSignature===signature.current)return;
  request.current?.abort();request.current=null;
  setOpen(false);setBusy(false);setSuggestions(null);setSelected([]);
 },[currentSignature,open]);
 useEffect(()=>()=>request.current?.abort(),[]);
 const cancel=()=>{
  request.current?.abort();request.current=null;
  setOpen(false);setBusy(false);setApplying(false);setError('');setSuggestions(null);setSelected([]);
 };
 const run=async()=>{
  if(disabled||words.length<8)return;
  if(open){cancel();return;}
  signature.current=currentSignature;
  const controller=new AbortController();request.current=controller;
  setOpen(true);setBusy(true);setError('');setSuggestions(null);
  try{
   const token=await getAccessToken();
   if(!token)throw new Error('Sign in to use AI Cleanup.');
   const response=await fetch(resolveApiBase()+'/api/ai-cleanup',{
    method:'POST',headers:{'Content-Type':'application/json',Authorization:'Bearer '+token},
    signal:AbortSignal.any([controller.signal,AbortSignal.timeout(100000)]),
    body:JSON.stringify({words:words.map(w=>w.text),protectedRanges})
   });
   const data=await response.json().catch(()=>({}));
   if(!response.ok)throw new Error(data.error||'AI Cleanup could not analyse this transcript.');
   if(controller.signal.aborted)return;
   if(currentSignature!==signature.current)throw new Error('The interview changed. Run cleanup again.');
   const next=safeCleanupSuggestions(data.suggestions,words,protectedRanges);
   setSuggestions(next);setSelected([]);
  }catch(e){if(!controller.signal.aborted)setError(message(e));}
  finally{if(request.current===controller){request.current=null;setBusy(false);}}
 };
 const apply=async()=>{
  if(!suggestions||!selected.length||applying)return;
  if(currentSignature!==signature.current){setError('The interview changed. Run cleanup again.');return;}
  setApplying(true);setError('');
  try{
   const chosen=suggestions.filter((_,i)=>selected.includes(i));
   await onApply(sourceId,chosen,words.map(w=>w.id),cleanupSignature(source));
   cancel();
  }catch(e){setError(message(e));setApplying(false);}
 };
 return <>
  <button type="button" className="ai-cleanup-trigger" onClick={run}
   aria-pressed={open} disabled={disabled||words.length<8}
   title="Identify likely crew chatter, false starts and expendable filler. Review before omitting.">
   <span aria-hidden="true">✦</span> AI Cleanup
  </button>
  {open&&<div className="ai-cleanup-review" role="region" aria-label="AI Cleanup suggestions">
   <div className="ai-cleanup-head">
    <div><strong>{busy?'Reviewing interview…':error?'AI Cleanup':suggestions?.length?
     suggestions.length+' suggested cuts':'No obvious cleanup needed'}</strong>
     <small>Review suggestions one by one · Nothing is selected automatically</small></div>
    <button type="button" className="ai-cleanup-close" disabled={applying} onClick={cancel} aria-label="Dismiss AI Cleanup"><FiX/></button>
   </div>
   {busy&&<p className="ai-cleanup-loading" role="status"><FiLoader aria-hidden="true"/> Finding obvious production chatter and false starts…</p>}
   {error&&<p className="ai-cleanup-error" role="alert">{error}</p>}
   {suggestions?.length>0&&<>
    <div className="ai-cleanup-items">
     {suggestions.map((item,index)=>{
      const excerpt=words.slice(item.start,item.end+1).map(w=>w.text).join(' ');
      const chosen=selected.includes(index);
      return <div className={'ai-cleanup-item'+(chosen?' is-chosen':'')} key={item.start+':'+item.end}>
       <label>
        <input type="checkbox" checked={chosen} disabled={applying}
         onChange={e=>setSelected(current=>e.target.checked?[...current,index]:current.filter(x=>x!==index))}/>
        <span className="ai-cleanup-item-content">
         <span className="ai-cleanup-item-meta"><strong>{labels[item.category]}</strong>
          <small>{formatTime(words[item.start].start)}–{formatTime(words[item.end].end)}</small></span>
         <span className="ai-cleanup-quote">“{excerpt.length>290?excerpt.slice(0,288)+'…':excerpt}”</span>
         <span className="ai-cleanup-reason">{item.reason}</span>
        </span>
       </label>
       {onPreview&&<button type="button" className="ai-cleanup-audition"
        onClick={()=>onPreview(words.slice(item.start,item.end+1))} disabled={applying}
        title="Audition the suggested cut"><FiPlay aria-hidden="true"/> Listen</button>}
      </div>;
     })}
    </div>
    <div className="ai-cleanup-actions">
     <span>{selected.length} of {suggestions.length} selected</span>
     <button type="button" className="ai-cleanup-dismiss" onClick={cancel} disabled={applying}>Dismiss</button>
     <button type="button" className="ai-cleanup-apply" onClick={apply}
      disabled={applying||!selected.length}><FiCheck aria-hidden="true"/>
      {applying?'Applying…':'Omit '+selected.length+' '+(selected.length===1?'passage':'passages')}</button>
    </div>
   </>}
   {suggestions?.length===0&&<button type="button" className="ai-cleanup-dismiss" onClick={cancel}>Close</button>}
   <small className="ai-cleanup-protection">Must Use and Assembly material is protected. Omissions can be restored in Transcript.</small>
  </div>}
 </>;
}
